import { useState } from 'react';
import { Button } from '../../../core/components/atoms/Button';
import { useForm } from 'react-hook-form';
import { ICreatePassword } from '../models/user.model';
import { Input } from '../../../core/components/atoms/Input';
import { Alert } from '../../../core/components/atoms/Alert';
import { Typography } from '../../../core/components/molecules/Typography';
import { Icon } from '../../../core/components/atoms/Icon';
import { useCreatePassword } from '../hooks/useCreatePassword';
import { useParams } from 'react-router-dom';
import Logo from '../../../assets/images/brand/logo-white-color.svg';
import LogoMobile from '../../../assets/images/brand/logo-black.png';
import BANNER_LOGIN from '../../../assets/images/banners/banner_login.png';

const CHECKOUT_URL =
  'https://checkout.aprovatotal.com.br/lead?oferta=a2mN40000004PxVIAU&productCode=plano-ate-o-enem-data-fixa-dezembro-vinte-sete?seller=performance&source=plataforma_aprova&utm_source=plataforma_aprova&utm_medium=organic&utm_campaign=tela-de-login-plataforma-aprova&utm_content=hiperlink-tela-de-login';

const SUPPORT_URL = 'mailto:suporte@aprovatotal.com.br';

const FEATURES = [
  { icon: 'rocket_launch', label: 'Estude de forma\nmais inteligente' },
  { icon: 'my_location', label: 'Foque no que\nrealmente cai' },
  { icon: 'emoji_events', label: 'Acompanhe sua\nevolução' },
];

export default function CreatePassword() {
  const { token } = useParams();

  const [inputType, setInputType] = useState<string>('password');
  const [inputTypeConfirm, setInputTypeConfirm] = useState<string>('password');
  const { mutate, isError, error, isLoading, isSuccess } = useCreatePassword();

  const {
    getValues,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ICreatePassword>();

  const onSubmit = (data: ICreatePassword) => {
    const body = { password: data.password, token: token || '' };
    mutate(body);
  };

  return (
    <div className='flex flex-col md:h-screen md:flex-row'>
      <div className='relative hidden items-center justify-center bg-brand-color-primary-dark md:flex md:h-screen md:w-1/2'>
        <div className='relative z-10 flex flex-col items-center px-4 md:px-8 lg:px-12'>
          <img src={Logo} alt='Imagem de Login' className='max-w-xs pb-[54px]' />
          <Typography
            size='h2'
            type='title'
            weight='bold'
            className='w-full max-w-[545px] px-4 text-center !text-white'
            style={{ color: '#ffffff' }}
          >
            Conquiste sua vaga na <br /> universidade em tempo recorde!
          </Typography>
          <Typography
            size='default'
            className='mt-[5px] px-4 text-center !text-white'
            style={{ color: '#ffffff' }}
          >
            O Aprova Total te leva até 10x mais rápido. <br />
            <span className='font-bold'>
              Experimente por 7 dias sem riscos!
            </span>
          </Typography>
          <Button
            variant='contained'
            size='large'
            className='mx-4 mt-[54px]'
            asLink={true}
            to={CHECKOUT_URL}
            target='_blank'
            style={{
              backgroundColor: '#FF5227',
              borderRadius: '8px',
              padding: '8px 16px',
            }}
          >
            Assine e dê o primeiro passo agora!
          </Button>
        </div>

        <div className='absolute inset-x-0 bottom-20 z-10 flex justify-center gap-12 px-4'>
          {FEATURES.map((feature) => (
            <div
              key={feature.icon}
              className='flex flex-col items-center gap-3 text-center'
            >
              <span className='flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#FF5227]'>
                <Icon
                  type='material'
                  Icon={feature.icon}
                  className='text-4xl text-[#FF5227]'
                />
              </span>
              <Typography
                size='default'
                className='whitespace-pre-line !text-white'
                style={{ color: '#ffffff' }}
              >
                {feature.label}
              </Typography>
            </div>
          ))}
        </div>

        <img
          src={BANNER_LOGIN}
          alt='Banner de Login'
          className='absolute inset-0 h-full w-full object-cover'
        />
      </div>

      <div className='relative flex w-full flex-col items-center justify-center overflow-auto bg-white p-6 md:h-screen md:w-1/2 md:gap-4 md:overflow-hidden md:p-10'>
        <img
          src={LogoMobile}
          alt='Aprova Total'
          className='mb-8 max-w-[180px] md:hidden'
        />

        <div className='w-full'>
          {isSuccess ? (
            <>
              <Typography
                size='h2'
                type='title'
                weight='bold'
                className='mb-2 text-center'
              >
                Tudo pronto para começar
              </Typography>

              <Alert
                variant='success'
                title='Sua senha foi criada'
                message='Você já pode acessar a plataforma'
                className='mb-6'
              />

              <Button variant='contained-tonal' size='large' full asLink to='/login'>
                Acessar plataforma
              </Button>
            </>
          ) : (
            <>
              <Typography
                size='h2'
                type='title'
                weight='bold'
                className='mb-2 text-center'
              >
                Crie uma senha
              </Typography>
              <Typography
                size='default'
                className='mb-6 text-center text-mono-500'
              >
                Defina uma senha segura para acessar sua conta.
              </Typography>

              <div className='mb-6 flex items-center gap-3 rounded-lg bg-[#FDE9E2] px-4 py-3'>
                <Icon
                  type='material'
                  Icon='verified_user'
                  className='text-xl text-[#FF5227]'
                />
                <Typography size='small' className='text-mono-700'>
                  A senha deve conter pelo menos 8 caracteres
                </Typography>
              </div>

              {isError && (
                <Alert
                  variant='error'
                  title='Erro ao criar'
                  message={
                    error?.toString() ||
                    'Houve algum problema ao criar sua senha'
                  }
                  className='mb-4'
                />
              )}

              <form
                onSubmit={handleSubmit(onSubmit)}
                className='flex flex-col gap-4 text-left'
              >
                <div className='flex flex-col gap-2'>
                  <label
                    htmlFor='password'
                    className='text-mono-color-black text-md text-bold'
                  >
                    Senha
                  </label>
                  <Input
                    {...register('password', {
                      required: true,
                      minLength: {
                        value: 8,
                        message: 'A senha digitada não contém 8 caracteres',
                      },
                    })}
                    placeholder='Digite uma senha'
                    helperText='Mínimo de 8 caracteres'
                    errorMessage={errors.password && errors.password.message}
                    type={inputType}
                    IconLeft={{
                      type: 'material',
                      Icon: 'lock',
                    }}
                    IconRight={{
                      type: 'material',
                      Icon:
                        inputType === 'password'
                          ? 'visibility_off'
                          : 'visibility',
                      className: 'cursor-pointer',
                      onClick: () => {
                        setInputType(
                          inputType === 'password' ? 'text' : 'password'
                        );
                      },
                    }}
                  />
                </div>
                <div className='flex flex-col gap-2'>
                  <label
                    htmlFor='confirmPassword'
                    className='text-mono-color-black text-md text-bold'
                  >
                    Confirme sua senha
                  </label>
                  <Input
                    {...register('confirmPassword', {
                      required: true,
                      validate: (value) =>
                        value === getValues('password') ||
                        'As senhas digitadas são diferentes',
                    })}
                    placeholder='Digite novamente sua senha'
                    errorMessage={
                      errors.confirmPassword && errors.confirmPassword.message
                    }
                    type={inputTypeConfirm}
                    IconLeft={{
                      type: 'material',
                      Icon: 'lock',
                    }}
                    IconRight={{
                      type: 'material',
                      Icon:
                        inputTypeConfirm === 'password'
                          ? 'visibility_off'
                          : 'visibility',
                      className: 'cursor-pointer',
                      onClick: () => {
                        setInputTypeConfirm(
                          inputTypeConfirm === 'password' ? 'text' : 'password'
                        );
                      },
                    }}
                  />
                </div>

                <Button
                  variant='contained'
                  size='large'
                  full
                  loading={isLoading}
                  style={{
                    backgroundColor: '#FF5227',
                    borderRadius: '8px',
                  }}
                >
                  Salvar senha
                </Button>
              </form>

              <div className='mt-6 flex items-center justify-center gap-2'>
                <Icon
                  type='material'
                  Icon='verified_user'
                  className='text-lg text-green-600'
                />
                <Typography size='small' className='text-mono-500'>
                  Seus dados estão protegidos com segurança avançada.
                </Typography>
              </div>

              <Typography size='small' className='mt-4 text-center text-mono-600'>
                Precisa de ajuda?{' '}
                <a
                  href={SUPPORT_URL}
                  className='font-medium'
                  style={{ color: '#FF5227' }}
                >
                  Fale com nosso suporte
                </a>
              </Typography>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
